/**
 * Role dashboard for the new console, rebuilt to Traci's review (23 Sep 2026) and the v108 prototype.
 *
 * Admin: her KPI cards only (Active Bookings, Events Next 31 Days, Bookings MTD with New / Booked & Cancelled /
 * Cancelled, Revenue MTD with All / New / Booked & Refunded / Refunds, Events Next 7 Days with outstanding actions),
 * Department Actions with a department dropdown so she can see every department's workload without opening its view,
 * and Upcoming Operations (events in the next 7 days: Ready, or what is still outstanding and who owns it). No
 * Business Snapshot. Other roles (View As): their KPI cards and their own department's action cards; Editing also gets
 * the Editing board.
 *
 * Every number comes from /api/admin/staging/dashboard (pure, tested modules) plus Nautica's Action Center from the
 * endpoint her workbench uses, so the dashboard and each department's own screen always agree.
 *
 * Card rows have an X (Traci, 25 Sep 2026): the owning department or Admin clears an item by hand after a warning,
 * and the clear is recorded in the Audit Log. Flag cards are resolved in the Notification Center instead. The
 * 24-Hour Cancellation — Refund card's Refund button opens the client's Billing view to issue the refund.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/admin/ui/dialog';
import { Button } from '@/components/admin/ui/button';
import { kpisForRole } from '@/lib/staging/kpis';
import StaffingProgress from '@/pages/admin/coordination/StaffingProgress';
import { DEPARTMENTS, withTalentItems } from '@/lib/staging/departmentActions';
import { ROLE_LABELS, STAGING_ROOT } from '@/lib/staging/roleNav';
import { stagingFetch, shortDate } from '../api';
import { useStaging } from '../StagingLayout';
import EditingPipeline from './EditingPipeline';
import CoordinationWorkbench from '@/pages/admin/CoordinationWorkbench';
import ClientBookingSearch from '../components/ClientBookingSearch';
import QueueRows, { type QueueCard, type QueueHandlers, type QueueRow } from '../components/QueueRows';
import PayTalentDialog from '../components/PayTalentDialog';
import AdminChatPanel from '@/components/admin/AdminChatPanel';

type Row = QueueRow;
type ActionCard = QueueCard;
type Upcoming = { booking_id: string; client_id: string | null; client_name: string | null; event_date: string; location: string | null; package_type: string | null; ready: boolean; actions: { department: string; label: string; detail?: string | null }[] };

const CARD_LINK: Record<string, string> = {
  notifications: `${STAGING_ROOT}/notifications`,
  insurance: `${STAGING_ROOT}/insurance`,
  billing: `${STAGING_ROOT}/billing`,
  waiting_room: `${STAGING_ROOT}/talent?tab=waiting`
};
const DEPT_LINK: Record<string, string> = {
  talent_coordinator: `${STAGING_ROOT}/coordination`,
  editing: `${STAGING_ROOT}/editing`,
  billing: `${STAGING_ROOT}/billing`,
  sales: `${STAGING_ROOT}/bookings`,
  admin: `${STAGING_ROOT}/insurance`
};
const DEPT_LABEL: Record<string, string> = Object.fromEntries(DEPARTMENTS.map((d) => [d.key, d.label]));
const bookingPath = (id?: string | null) => (id ? `${STAGING_ROOT}/bookings/${id}` : null);
const pkgLabel = (t?: string | null) => ({ photo: 'Photo', photoOnly: 'Photo', photoVideo: 'Photo + Video', videoOnly: 'Video', engagement: 'Engagement' } as Record<string, string>)[String(t || '')] || (t ? String(t) : 'Booking');

const KPI_LINK: Record<string, { to: string; label: string }> = {
  bookings: { to: `${STAGING_ROOT}/bookings`, label: 'View records →' },
  billing: { to: `${STAGING_ROOT}/billing`, label: 'View records →' },
  editing: { to: `${STAGING_ROOT}/editing`, label: 'View queue →' },
  unfinished: { to: `${STAGING_ROOT}/reports#report-unfinished`, label: 'View checkouts →' }
};

function KpiCard({ card }: { card: any }) {
  const [view, setView] = useState<number>(card.default_view ?? 0);
  const views = card.views || [];
  const value = views.length ? views[view]?.value : card.value;
  const link = card.key === 'active_bookings' ? { to: `${STAGING_ROOT}/bookings`, label: 'View active →' } : KPI_LINK[card.queue];
  return (
    <div className="staging-kpi rounded-xl border bg-card p-4 shadow-sm" title={card.rule}>
      <div className="text-sm text-muted-foreground">{card.label}</div>
      {views.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1" role="tablist" aria-label={`${card.label} view`}>
          {views.map((v: any, i: number) => (
            <button key={v.key} type="button" role="tab" aria-selected={view === i} onClick={() => setView(i)}
              className={`rounded-full border px-2 py-0.5 text-[10px] leading-4 ${view === i ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground'}`}>{v.label}</button>
          ))}
        </div>
      )}
      <strong className="block mt-2">{value ?? '—'}</strong>
      {card.queue === 'upcoming' && card.hint
        ? <button type="button" className="mt-1 text-[11px] text-primary hover:underline" onClick={() => document.getElementById('upcoming-operations')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })}>{card.hint}</button>
        : card.hint && <div className="mt-1 text-[11px] text-muted-foreground leading-snug">{card.hint}</div>}
      {link && card.queue !== 'upcoming' && <Link to={link.to} className="mt-1 block text-[11px] text-primary hover:underline">{link.label}</Link>}
    </div>
  );
}

export default function StagingDashboard() {
  const { role, actualRole } = useStaging();
  const [data, setData] = useState<any | null>(null);
  const [talentItems, setTalentItems] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [dept, setDept] = useState<string>(role === 'admin' ? 'admin' : role);
  const [openCard, setOpenCard] = useState<ActionCard | null>(null);
  const [openOp, setOpenOp] = useState<Upcoming | null>(null);
  const [payRow, setPayRow] = useState<Row | null>(null);
  const [chatWith, setChatWith] = useState<Row | null>(null);

  useEffect(() => { setDept(role === 'admin' ? 'admin' : role); }, [role]);

  const load = async () => {
    setLoading(true);
    try {
      const [d, a] = await Promise.all([
        stagingFetch('/api/admin/staging/dashboard'),
        stagingFetch('/api/admin/coordination/actions?horizon=120').catch(() => null)
      ]);
      setData(d);
      setTalentItems(a?.items || null);
      setError(null);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const merged = useMemo(() => (data ? withTalentItems({ departments: data.departments, upcoming: data.upcoming }, talentItems, data.dismissed || []) : null), [data, talentItems]);
  // Keep an open card in step with a reload (after a clear)
  useEffect(() => {
    if (!openCard || !merged) return;
    const fresh = (merged.departments[dept] || []).find((c: ActionCard) => c.key === openCard.key);
    if (fresh && fresh !== openCard) setOpenCard(fresh);
  }, [merged]); // eslint-disable-line react-hooks/exhaustive-deps
  const canClearHere = actualRole === 'admin' || role === dept;
  const markGhl = async (card: ActionCard, r: Row) => {
    try {
      await stagingFetch('/api/admin/staging/actions/dismiss', { method: 'POST', body: JSON.stringify({ department: dept, card_key: card.key, card_title: card.title, item_key: r.item_key, booking_id: r.booking_id || null, label: [r.client_name, r.detail].filter(Boolean).join(' · '), reason: 'Entered in GHL' }) });
      toast.success(`${r.client_name || 'Booking'} marked entered in GHL`);
      await load();
    } catch (e: any) { toast.error(e.message || String(e)); }
  };
  const clearRow = async (card: ActionCard, r: Row) => {
    const who = r.client_name || 'this item';
    const warning = card.key === 'refund_24h'
      ? `Clear ${who} from "${card.title}" WITHOUT issuing a refund?\n\nOnly do this when no refund is owed. It will not come back on this card, and the clear is recorded in the Audit Log with your name.`
      : `Clear ${who} from "${card.title}"?\n\nUse this when the expected action isn't needed. It will not come back on this card, and the clear is recorded in the Audit Log with your name.`;
    if (!window.confirm(warning)) return;
    try {
      await stagingFetch('/api/admin/staging/actions/dismiss', { method: 'POST', body: JSON.stringify({ department: dept, card_key: card.key, card_title: card.title, item_key: r.item_key, booking_id: r.booking_id || null, label: [r.client_name, r.detail].filter(Boolean).join(' · ') }) });
      toast.success('Cleared and recorded in the Audit Log');
      await load();
    } catch (e: any) { toast.error(e.message || String(e)); }
  };
  const setQbo = async (r: Row, status: 'invited' | 'onboarded') => {
    if (status === 'onboarded' && !window.confirm(`Mark ${r.talent_name || 'this talent'} as onboarded in QuickBooks? They leave this queue.`)) return;
    try {
      await stagingFetch(`/api/admin/staging/talent/${r.talent_id}/qbo`, { method: 'POST', body: JSON.stringify({ status }) });
      toast.success(status === 'invited' ? 'QBO invite marked sent' : 'Marked onboarded');
      await load();
    } catch (e: any) { toast.error(e.message || String(e)); }
  };
  const handlersFor = (card: ActionCard): QueueHandlers => ({
    onClear: card.clearable && canClearHere ? (r) => clearRow(card, r) : undefined,
    onGhl: card.key === 'ghl_entry' && canClearHere ? (r) => markGhl(card, r) : undefined,
    onPay: card.key === 'pay_talent' && ['admin', 'billing'].includes(actualRole) ? (r) => setPayRow(r) : undefined,
    onQbo: card.key === 'qbo_invite' && ['admin', 'billing'].includes(actualRole) ? setQbo : undefined,
    onMessage: card.key === 'overdue_follow_up' ? (r) => setChatWith(r) : undefined
  });
  // Talent's "Events Next 7 Days" counts only events with an outstanding Talent action (v84 note, P 5340); the Action
  // Center items arrive here, so the count is made from the merged Upcoming Operations.
  const talentNext7 = merged ? merged.upcoming.filter((u: Upcoming) => u.actions.some((a) => a.department === 'talent_coordinator')).length : null;
  // Traci, Oct 2026: "Next 7 days with outstanding" showed a different number from Upcoming Operations below it; the
  // server counted before Nautica's items were merged in. Count from the same merged list the page shows.
  const outstandingNext7 = merged ? merged.upcoming.filter((u: Upcoming) => !u.ready).length : null;
  const cards = data ? kpisForRole(data.kpis, role).filter(Boolean).map((c: any) => (role === 'talent_coordinator' && c.key === 'events_next_7' && talentNext7 !== null
    ? { ...c, value: talentNext7, rule: 'events in the next 7 days with an outstanding Talent action', hint: 'with outstanding Talent actions' }
    : c.key === 'events_next_7_outstanding' && outstandingNext7 !== null ? { ...c, value: merged.upcoming.length, hint: `${outstandingNext7} with outstanding actions`, outstanding: outstandingNext7 } : c)) : [];
  const isAdminView = role === 'admin';
  const deptCards: ActionCard[] = merged ? (merged.departments[dept] || []) : [];
  const upcoming: Upcoming[] = merged?.upcoming || [];

  // Talent (Traci, 28 Sep 2026): Nautica's Dashboard is her Coordination workbench, with her Flags and Waiting Room
  // (Applications Pending) cards on top. Admin keeps the Department Actions view and the Coordination tab.
  if (role === 'talent_coordinator') {
    const top = deptCards.filter((c) => c.key === 'talent_flags' || c.key === 'applications_pending');
    return (
      <div>
        <div className="px-8 pt-8 space-y-4">
          {error && <p className="text-sm text-destructive">Could not load: {error}</p>}
          {!merged && !error && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
          <ClientBookingSearch />
          {data && cards.length > 0 && (
            <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
              {cards.map((c: any) => <KpiCard key={c.key} card={c} />)}
            </div>
          )}
          {/* Traci's doc, Oct 2026: "KPI additions - Talent": Yearly Progress and Timeframe Booking Status */}
          <StaffingProgress />
          {top.length > 0 && (
            <div className="grid gap-4 lg:grid-cols-2">
              {top.map((c) => (
                <section key={c.key} aria-label={c.title} className="rounded-xl border bg-card p-4 shadow-sm">
                  <div className="mb-1.5 flex items-baseline justify-between gap-2">
                    <h3 className="text-sm font-semibold">{c.title}{c.status === 'live' ? <span className="ml-2 rounded-full border bg-card px-2 text-xs tabular-nums">{c.count}</span> : null}</h3>
                    {c.note && c.status === 'live' && <span className="text-[11px] text-muted-foreground">{c.note}</span>}
                  </div>
                  <QueueRows card={c} dept={dept} handlers={handlersFor(c)} maxHeight="max-h-[230px]" />
                </section>
              ))}
            </div>
          )}
        </div>
        <CoordinationWorkbench />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{ROLE_LABELS[role] || role} dashboard</div>
          <p className="text-sm text-muted-foreground mt-1">Counts use the same rules as each department&apos;s own queue. Hover a card for its rule.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
      </div>

      {error && <p className="text-sm text-destructive">Could not load: {error}</p>}
      {!data && !error && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}

      {data && (
        <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
          {cards.map((c: any) => <KpiCard key={c.key} card={c} />)}
        </div>
      )}

      {merged && (
        <section className="rounded-xl border bg-card p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Department Actions</h2>
              <p className="text-sm text-muted-foreground">{isAdminView ? 'What needs to be done? Select a department to supervise its current work.' : `${ROLE_LABELS[role] || role} work requiring action.`}</p>
            </div>
            {isAdminView && actualRole === 'admin' && (
              <label className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Department</span>
                <select aria-label="Department" className="h-9 rounded-md border bg-background px-2 text-sm" value={dept} onChange={(e) => setDept(e.target.value)}>
                  {DEPARTMENTS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
              </label>
            )}
          </div>
          {isAdminView ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {deptCards.map((c) => (
                <button key={c.key} type="button" onClick={() => setOpenCard(c)} disabled={c.status !== 'live'}
                  className={`text-left rounded-lg border p-4 transition ${c.status === 'live' ? 'hover:bg-accent/50 bg-background' : 'bg-muted/40 cursor-default'}`}>
                  <div className="text-sm font-semibold leading-snug">{c.title}</div>
                  <div className="mt-2 text-2xl font-semibold tabular-nums">{c.status === 'live' ? c.count : '—'}</div>
                  <div className="mt-1 text-[11px] text-muted-foreground leading-snug">{c.status === 'live' ? (c.count ? `Open ${c.title} →` : 'Nothing waiting') : c.note}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              {deptCards.filter((c) => role !== 'editing' || c.key.endsWith('_flags')).map((c) => (
                <section key={c.key} aria-label={c.title}>
                  <div className="mb-1.5 flex items-baseline justify-between gap-2">
                    <h3 className="text-sm font-semibold">{c.title}{c.status === 'live' ? <span className="ml-2 rounded-full border bg-card px-2 text-xs tabular-nums">{c.count}</span> : null}</h3>
                    {c.note && c.status === 'live' && <span className="text-[11px] text-muted-foreground">{c.note}</span>}
                  </div>
                  <QueueRows card={c} dept={dept} handlers={handlersFor(c)} maxHeight="max-h-[230px]" />
                </section>
              ))}
            </div>
          )}
          {DEPT_LINK[dept] && <div className="mt-3 text-right"><Link to={DEPT_LINK[dept]} className="text-sm text-primary hover:underline">Go to {DEPT_LABEL[dept] || dept}&apos;s full view →</Link></div>}
        </section>
      )}

      {merged && isAdminView && (
        <section id="upcoming-operations" className="rounded-xl border bg-card p-5 shadow-sm">
          <h2 className="text-lg font-semibold">Upcoming Operations</h2>
          <p className="text-sm text-muted-foreground">What&apos;s happening in the next 7 days, and do any bookings still have outstanding actions?</p>
          <div className="mt-4 max-h-[440px] overflow-y-auto rounded-md border divide-y">
            {upcoming.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No events in the next 7 days.</p>}
            {upcoming.map((u) => (
              <button key={u.booking_id} type="button" onClick={() => setOpenOp(u)} className="w-full grid grid-cols-[90px_minmax(0,1fr)_140px_170px_20px] items-center gap-3 px-4 py-2.5 text-left hover:bg-accent/40">
                <span className="font-semibold tabular-nums text-sm">{shortDate(u.event_date).replace(/, \d{4}$/, '').toUpperCase()}</span>
                <span className="min-w-0"><span className="block font-medium truncate">{u.client_name || 'Client'}</span><span className="block text-[11px] text-muted-foreground truncate font-mono">{u.booking_id.slice(0, 8).toUpperCase()}</span></span>
                <span className="text-sm text-muted-foreground">{pkgLabel(u.package_type)}</span>
                <span className={`justify-self-start rounded-full border px-2 py-0.5 text-[11px] font-semibold ${u.ready ? 'border-emerald-300 text-emerald-800 bg-emerald-50' : 'border-amber-300 text-amber-900 bg-amber-50'}`}>{u.ready ? 'READY' : `${u.actions.length} OUTSTANDING ACTION${u.actions.length === 1 ? '' : 'S'}`}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        </section>
      )}

      {role === 'editing' && <EditingPipeline embedded />}

      <Dialog open={!!openCard} onOpenChange={(o) => !o && setOpenCard(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{openCard?.title}</DialogTitle>
            <DialogDescription>{DEPT_LABEL[dept] || dept} · {openCard?.count ?? 0} item{openCard?.count === 1 ? '' : 's'}{openCard?.note ? ` · ${openCard.note}` : ''}</DialogDescription>
          </DialogHeader>
          {openCard && <QueueRows card={openCard} dept={dept} handlers={handlersFor(openCard)} />}
          {openCard && (CARD_LINK[openCard.link || ''] || DEPT_LINK[dept]) && (
            <div className="text-right"><Link to={CARD_LINK[openCard.link || ''] || DEPT_LINK[dept]} className="text-sm text-primary hover:underline" onClick={() => setOpenCard(null)}>Open the full queue →</Link></div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!openOp} onOpenChange={(o) => !o && setOpenOp(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{openOp?.client_id ? <Link className="hover:underline" to={`${STAGING_ROOT}/clients/${openOp.client_id}`}>{openOp.client_name || 'Client'}</Link> : (openOp?.client_name || 'Client')}{openOp && <> · <Link className="font-mono text-base hover:underline" to={bookingPath(openOp.booking_id)!}>{openOp.booking_id.slice(0, 8).toUpperCase()}</Link></>}</DialogTitle>
            <DialogDescription>Upcoming Operations</DialogDescription>
          </DialogHeader>
          {openOp && (
            <dl className="grid grid-cols-3 gap-2 text-sm">
              <div className="rounded-md border p-2"><dt className="text-[10px] uppercase text-muted-foreground">Event</dt><dd>{pkgLabel(openOp.package_type)}{openOp.location ? ` · ${openOp.location}` : ''}</dd></div>
              <div className="rounded-md border p-2"><dt className="text-[10px] uppercase text-muted-foreground">Date</dt><dd>{shortDate(openOp.event_date)}</dd></div>
              <div className="rounded-md border p-2"><dt className="text-[10px] uppercase text-muted-foreground">Readiness</dt><dd>{openOp.ready ? 'Ready' : `${openOp.actions.length} outstanding`}</dd></div>
            </dl>
          )}
          {openOp && (openOp.ready ? (
            <p className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">✓ READY. No booking-linked department actions are open for this event.</p>
          ) : (
            <div className="space-y-3">
              {DEPARTMENTS.filter((d) => openOp.actions.some((a) => a.department === d.key)).map((d) => (
                <div key={d.key} className="rounded-md border p-3">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{d.label} · {openOp.actions.filter((a) => a.department === d.key).length} action(s)</div>
                  <ul className="mt-1 space-y-1">
                    {openOp.actions.filter((a) => a.department === d.key).map((a, i) => (
                      <li key={i} className="flex items-start justify-between gap-2 text-sm"><span><b className="font-medium">{a.label}</b>{a.detail && <span className="block text-xs text-muted-foreground">{a.detail}</span>}</span>{d.key === 'admin' ? <Button asChild variant="outline" size="sm" className="h-7 shrink-0 text-xs"><Link to={`${STAGING_ROOT}/insurance`}>Open Insurance</Link></Button> : <Button asChild variant="outline" size="sm" className="h-7 shrink-0 text-xs"><Link to={d.key === 'talent_coordinator' ? `${STAGING_ROOT}/coordination/${openOp.booking_id}` : bookingPath(openOp.booking_id)!}>Open Booking</Link></Button>}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground">Upcoming Operations is a readiness view only. The work itself stays in the department&apos;s queue or on the booking.</p>
        </DialogContent>
      </Dialog>
      <PayTalentDialog row={payRow as any} onClose={() => setPayRow(null)} onPaid={() => { setPayRow(null); load(); }} />
      {chatWith?.client_id && <AdminChatPanel userId={chatWith.client_id} userName={chatWith.client_name || 'Client'} userRole="customer" onClose={() => setChatWith(null)} />}
    </div>
  );
}
